import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  IconButton,
  Collapse,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CardActionArea,
  Tooltip,
  Portal,
  Skeleton,
} from "@mui/material";
import {
  useRobots,
  useTranslations,
  useOrganizations,
  useSnackbar,
  useVersions,
  useUser,
  useLLM,
} from "hooks";
import { organizationsApi, robotsApi } from "api";
import AvatarRem from "./AvatarRem";
import {
  Delete,
  Edit,
  ExpandLess,
  ExpandMore,
  PrecisionManufacturing,
  Add,
  Backup,
  DataObject,
} from "@mui/icons-material";
import { RobotDialog } from "./dialogs";
import { Robot } from "hooks/useRobots";
import { getTranslation } from "common";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import AiDrawer from "./aiChat/AiDrawer";
import AiFab from "./aiChat/AiFab";
import { LoadingButton } from "@mui/lab";
import { RobotFormRender, RobotValuesEditorRef } from "./RobotFormRender";

interface UserPersonalRobotsProps {
  onRobotUpdate: () => Promise<void>;
  searchedRobots: Robot[]; //per la ricerca
}

interface Category {
  id: number;
  description: string;
}

const UserPersonalRobots: React.FC<UserPersonalRobotsProps> = ({
  onRobotUpdate,
  searchedRobots,
}) => {
  const navigate = useNavigate();
  const user = useUser();

  const {
    aiAnswers,
    handleSubmit: _handleSubmitLLM,
    openAI,
    setOpenAI,
    llmInputRef,
    loading: LLMLoading,
    modelInputRef,
    stopLLM,
    sessions,
    currentSessionId,
    startNewSession,
    loadSession,
    deleteSession,
    renameSession,
    shareSession,
    currentIdSession,
    handleRetry,
    refreshSessions,
    modelProvider,
    setModelProvider,
    modelName,
    setModelName,
  } = useLLM("robot/request");
  const { getVersion, updateVersion } = useVersions();
  const [userRobots, getUserRobots, setUserRobots] = useRobots("user"); //hook modificato leggermente per accettare un terzo elemento
  const [userOrganizations] = useOrganizations();
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const [loadingOrgs, setLoadingOrgs] = useState<number[]>([]);
  const [loadingRobots, setLoadingRobots] = useState(true);
  const [editRobot, setEditRobot] = useState<Robot | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<Robot | undefined>(
    undefined
  );
  const [expandedRobots, setExpandedRobots] = useState<number[]>([]);
  const [create, setCreate] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedRobot, setSelectedRobot] = useState<Robot | undefined>(
    undefined
  );
  const [targetOrg, setTargetOrg] = useState<{ id: number; name: string } | null>(
    null
  );
  const [robotSchema, setRobotSchema] = useState<any>(null);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const editorRef = useRef<RobotValuesEditorRef>(null);
  const apiSchemaRef = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [shouldHaveZIndex, setShouldHaveZIndex] = useState(false);

  useEffect(() => {
    const fetchRobots = async () => {
      setLoadingRobots(true);
      try {
        await getUserRobots();
      } catch { }
      setLoadingRobots(false);
    };
    fetchRobots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleOrganizations = (robotId: number) => {
    setExpandedRobots((prev) =>
      prev.includes(robotId)
        ? prev.filter((id) => id !== robotId)
        : [...prev, robotId]
    );
  };

  const handleEditRobot = (robot: Robot) => {
    setEditRobot(robot);
  };

  const handleDeleteRobot = async () => {
    if (!confirmDelete) return;
    try {
      const { data } = await robotsApi.delete(`${confirmDelete.id}`);
      sendSnack({
        message: data.responsemessage,
        type: "success",
      });
      setConfirmDelete(undefined);
      await getUserRobots();
      await onRobotUpdate();
    } catch { }
  };

  const onSaveRobot = async () => {
    await getUserRobots();
    await onRobotUpdate();
    setEditRobot(undefined);
  };

  const handleOpenAddDialog = async (robot: Robot, org: any) => {
    setSelectedRobot(robot);
    setTargetOrg(org);
    setAddDialogOpen(true);
    try {
      const { data } = await robotsApi.get(`/${robot.id}`);
      if (data.orgjsonschema) {
        const parsedSchema = JSON.parse(data.orgjsonschema);
        setRobotSchema(parsedSchema);
        apiSchemaRef.current = parsedSchema;

        let hasRequiredFields = false;
        Object.values(parsedSchema).forEach((section: any) => {
          if (section && section.properties) {
            Object.values(section.properties).forEach((prop: any) => {
              if (prop.required) {
                hasRequiredFields = true;
              }
            });
          }
        });
        setIsFormValid(!hasRequiredFields);
      } else {
        setRobotSchema(null);
        setIsFormValid(true);
      }
    } catch (error) {
      console.error("Error processing robot schema:", error);
      setRobotSchema(null);
      setIsFormValid(true);
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSelectedRobot(undefined);
    setTargetOrg(null);
    setRobotSchema(null);
    apiSchemaRef.current = null;
    setIsFormValid(false);
  };

  const handleAddRobot = async () => {
    if (!selectedRobot || !targetOrg) return;

    setLoading(true);
    try {
      const formData = editorRef.current?.getFormData();
      const response = await organizationsApi.put(
        `${targetOrg.id}/robots/${selectedRobot.id}`
      );

      if (
        formData &&
        apiSchemaRef.current &&
        Object.keys(formData).length > 0
      ) {
        try {
          await organizationsApi.post(
            `/${targetOrg.id}/robots/${selectedRobot.id}/settings`,
            {
              orgjsonschema: formData,
            }
          );
        } catch (e) {
          console.error("Error saving robot settings:", e);
        }
      }
      sendSnack({
        message: response.data.responsemessage,
        type: "success",
      });

      await getUserRobots();
      await onRobotUpdate();
      handleCloseAddDialog();
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleChangeOrganization = async (robotId: number, orgId: number) => {
    const robot = userRobots.find((robot) => robot.id === robotId);
    if (!robot) return;

    const isRobotInOrg = robot.organizations.some(
      (organization) => String(organization.id) === String(orgId)
    );

    if (isRobotInOrg) {
      setLoadingOrgs((currentLoadingOrgs) => [...currentLoadingOrgs, orgId]);
      try {
        const response = await organizationsApi.delete(
          `${orgId}/robots/${robotId}`
        );
        sendSnack({
          message: response.data.responsemessage,
          type: "success",
        });
        await getUserRobots();
        await onRobotUpdate();
      } catch {
      } finally {
        setLoadingOrgs((currentLoadingOrgs) =>
          currentLoadingOrgs.filter((id) => id !== orgId)
        );
      }
    } else {
      const org = userOrganizations.find((o) => o.id === orgId);
      if (!org) return;
      handleOpenAddDialog(robot, org);
    }
  };

  const publishRobot = async (robotId: number) => {
    try {
      const response = await robotsApi.put(`/${robotId}/publish`);
      if (response.data.success) {
        sendSnack({
          message: response.data.responsemessage,
          type: "success",
        });
        onRobotUpdate();
      }
      return true;
    } catch { }
  };

  const depublishRobot = async (robotId: number) => {
    const robot = userRobots.find((robot) => robot.id === robotId);
    if (robot && robot.organizations.length > 0) {
      sendSnack({
        message: getTranslation(
          translations,
          "robots.errors.depublish.organizations"
        ),
        type: "error",
      });
      return false;
    }
    try {
      const response = await robotsApi.delete(`/${robotId}/depublish`);
      if (response.data.success) {
        sendSnack({
          message: response.data.responsemessage,
          type: "success",
        });
        onRobotUpdate();
      }

      return true;
    } catch { }
  };

  const handlePublishRobot = async (robotId: number, published: boolean) => {
    const isPublished = published
      ? await publishRobot(robotId)
      : await depublishRobot(robotId);
    if (isPublished) {
      setUserRobots((prevRobots) =>
        prevRobots.map((robot) =>
          robot.id === robotId ? { ...robot, published } : robot
        )
      );
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await robotsApi.get("/categories");
        if (data) {
          setCategories(data.categories);
        }
      } catch { }
    };
    fetchCategories();
  }, []);

  const handleSubmitLLM = async () => {
    _handleSubmitLLM({ request_type: "robot_code", robotId: editRobot?.id });
  };

  if (loadingRobots) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Skeleton variant="rectangular" height={174} sx={{ borderRadius: 2 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <>
      <Portal container={document.body}>
        <AiDrawer
          LLMLoading={LLMLoading}
          aiAnswers={aiAnswers}
          handleSubmitLLM={handleSubmitLLM}
          llmInputRef={llmInputRef}
          modelInputRef={modelInputRef}
          openAI={openAI}
          setOpenAI={setOpenAI}
          zIndex={shouldHaveZIndex ? 99999 : undefined}
          stopLLM={stopLLM}
          sessions={sessions}
          currentSessionId={currentSessionId}
          startNewSession={startNewSession}
          loadSession={loadSession}
          deleteSession={deleteSession}
          renameSession={renameSession}
          shareSession={shareSession}
          currentIdSession={currentIdSession}
          handleRetry={handleRetry}
          modelProvider={modelProvider}
          setModelProvider={setModelProvider}
          modelName={modelName}
          setModelName={setModelName}
          refreshSessions={refreshSessions}
        />
      </Portal>
      <AiFab openAI={openAI} setOpenAI={setOpenAI} />

      {userRobots.length === 0 && (
        <Typography variant="h6" sx={{ textAlign: "center" }}>
          {getTranslation(translations, "no.robots")}
        </Typography>
      )}
      <Grid container spacing={2}>
        {searchedRobots.map(
          (
            robot //dalla ricerca che la fa il padre
          ) => (
            <Grid item sm={6} md={4} lg={3} xs={12} key={`robot-${robot.id}`}>
              <Card
                elevation={3}
                variant="outlined"
                sx={{
                  height: "auto",
                  minHeight: 125,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "15px",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.24)"
                      : "divider",
                  bgcolor: "background.paper",
                }}
              >
                <CardHeader
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "0fr minmax(20px, 1fr) minmax(50px, 0fr)",
                  }}
                  title={robot.name}
                  subheader={
                    categories.find((c) => c.id === robot.idcategory)
                      ?.description ||
                    getTranslation(translations, "generic.no.category")
                  }
                  titleTypographyProps={{
                    sx: {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                  avatar={
                    <AvatarRem
                      sx={{ width: 80, height: 80 }}
                      key={`${robot.id}-${getVersion(robot.id)}`}
                      icon={robot.picture || undefined}
                    >
                      <PrecisionManufacturing
                        sx={{ width: "50%", height: "50%" }}
                      />
                    </AvatarRem>
                  }
                  action={
                    <Box>
                      <Tooltip title={getTranslation(translations, "generic.edit")}>
                        <IconButton
                          onClick={() => handleEditRobot(robot)}
                          size="small"
                          id="edit-robot-btn"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {robot.iduserowner === user?.userId && (
                        <Tooltip title={getTranslation(translations, "property.editor")}>
                          <IconButton
                            onClick={() => navigate(`/dashboard/robots/${robot.id}/form-builder`)}
                            size="small"
                            id="build-robot-form-btn"
                          >
                            <DataObject fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!robot.published && (
                        <Tooltip title={getTranslation(translations, "generic.button.delete")}>
                          <IconButton
                            onClick={() => setConfirmDelete(robot)}
                            size="small"
                            id="delete-robot-btn"
                            data-testid={`delete-robot-${robot.id}`}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                />
                <CardActions
                  disableSpacing
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Button
                    id="show-robot-details"
                    onClick={() => toggleOrganizations(robot.id)}
                    endIcon={
                      expandedRobots.includes(robot.id) ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )
                    }
                  >
                    {expandedRobots.includes(robot.id)
                      ? getTranslation(translations, "hide.details")
                      : getTranslation(translations, "show.details")}
                  </Button>
                  <Tooltip
                    title={getTranslation(
                      translations,
                      robot.published ? "robot.published" : "robot.unpublished"
                    )}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="publish-unpublish"
                          data-testid={`publish-unpublish-${robot.id}`}
                          size="small"
                          checked={robot.published}
                          onChange={(e) => {
                            handlePublishRobot(robot.id, e.target.checked);
                          }}
                          icon={<Backup />}
                          checkedIcon={<CloudDoneIcon />}
                        />
                      }
                      label=""
                    />
                  </Tooltip>
                </CardActions>
                <Collapse in={expandedRobots.includes(robot.id)}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {robot.description}
                    </Typography>
                    <Stack direction="column">
                      <Typography sx={{ mt: 2, fontWeight: "bold" }}>
                        {getTranslation(
                          translations,
                          "menu.left.organizations"
                        )}
                      </Typography>
                      {userOrganizations
                        .filter(({ administrator }) => administrator)
                        .map((org) => (
                          <FormControlLabel
                            key={org.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={robot.organizations.some(
                                  (organization) =>
                                    String(organization.id) === String(org.id)
                                )}
                                onChange={() =>
                                  handleChangeOrganization(
                                    robot.id,
                                    Number(org.id)
                                  )
                                }
                                disabled={loadingOrgs.includes(
                                  org.id as number
                                ) || !robot.published}
                              />
                            }
                            label={org.name}
                          />
                        ))}
                    </Stack>
                  </CardContent>
                </Collapse>
              </Card>
            </Grid>
          )
        )}
        <Grid item sm={6} md={4} lg={3} xs={12} key="robot-add">
          <Card
            id="new-robot-btn"
            elevation={0}
            variant="outlined"
            sx={{
              border: "2px dashed grey",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.32)"
                  : "grey",
              minHeight: 165,
              display: "flex",
              flexDirection: "column",
              borderRadius: "15px",
            }}
          >
            <CardActionArea
              data-testid="add-robot-btn"
              sx={{
                display: "flex",
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => setCreate(true)}
            >
              <Typography variant="body2">
                {getTranslation(translations, "generic.add")}
              </Typography>
              <Add htmlColor="grey" />
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(undefined)}
      >
        <DialogTitle>
          {getTranslation(translations, "generic.button.confirm")}
        </DialogTitle>
        <DialogContent>
          {getTranslation(translations, "robots.confirm.delete")}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDelete(undefined)}
            variant="contained"
            size="small"
          >
            {getTranslation(translations, "generic.button.cancel")}
          </Button>
          <Button
            onClick={handleDeleteRobot}
            variant="contained"
            size="small"
            id="confirm-delete-robot-btn"
          >
            {getTranslation(translations, "generic.button.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
      <RobotDialog
        updateVersion={updateVersion}
        onSave={onSaveRobot}
        open={Boolean(editRobot)}
        defaultRobot={editRobot}
        onClose={() => setEditRobot(undefined)}
        userId={user?.userId.toString()}
        onOpenAi={() => {
          setShouldHaveZIndex(true);
          setOpenAI(true);
        }}
      />
      <RobotDialog
        updateVersion={updateVersion}
        onSave={onSaveRobot}
        open={create}
        onClose={() => setCreate(false)}
        userId={user?.userId.toString()}
      />
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {getTranslation(translations, "generic.button.confirm")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {getTranslation(translations, "generic.add")}{" "}
            <span style={{ fontWeight: "bold" }}>{selectedRobot?.name}</span>{" "}
            {getTranslation(translations, "generic.to")}{" "}
            <span style={{ fontWeight: "bold" }}>{targetOrg?.name}</span>?
          </Typography>

          {robotSchema && Object.keys(robotSchema).length > 0 && (
            <Box mt={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {getTranslation(translations, "robot.configure_values")}
              </Typography>
              <RobotFormRender
                ref={editorRef}
                schema={robotSchema}
                initialData={{}}
                showSaveButton={false}
                onValidationChange={setIsFormValid}
                hideSectionStyling={false}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} color="primary">
            {getTranslation(translations, "generic.button.cancel")}
          </Button>
          <LoadingButton
            onClick={handleAddRobot}
            color="primary"
            variant="contained"
            disabled={loading || !isFormValid}
            loading={loading}
            id="confirm-add-robot-to-org"
          >
            {getTranslation(translations, "generic.add")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserPersonalRobots;
